import { NumberInput, Text } from '@mantine/core'
import { PATIENT_INVOICE_KIND_LABELS } from '@/model/patient-invoice-kind'
import { PILL_STYLE_AMBER, PILL_STYLE_GREEN, PILL_STYLE_RED, formatCents } from '../utils'
import {
  computeAdjustmentCents,
  computeFeesCents,
  computeInsuranceCents,
  breakdownError,
  breakdownOverpaid,
  insuranceError,
  KIND_ORDER,
  lockedHint,
  lockedKinds,
  type EobAmounts,
  type PatientBreakdown,
  type PatientInvoiceInfo,
} from './eob-amounts'
import { StatusPill } from './StatusPill'

function BalanceBadge({
  patientRespCents,
  patientPaidCents,
}: {
  patientRespCents: number | null
  patientPaidCents: number | null
}) {
  if (patientRespCents === null) return null
  const balance = patientRespCents - (patientPaidCents ?? 0)
  if (balance === 0) return <StatusPill style={PILL_STYLE_GREEN}>✓ Patient balance $0</StatusPill>
  if (balance > 0) {
    return <StatusPill style={PILL_STYLE_AMBER}>Patient owes {formatCents(balance)}</StatusPill>
  }
  return (
    <StatusPill style={PILL_STYLE_RED}>Credit {formatCents(-balance)} — needs review</StatusPill>
  )
}

function centsToDollars(cents: number | null): number | '' {
  return cents === null ? '' : cents / 100
}

function dollarsToCents(value: number | string): number | null {
  if (value === '' || value === '-') return null
  const dollars = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(dollars) ? Math.round(dollars * 100) : null
}

type EobAmountsFormProps = {
  /** The claim's REMAINING charge (charge − Σ paid); null when the sweep has no Claim yet. */
  providerChargeCents: number | null
  /** Σ processed patient payments of every kind; null when none settled. */
  patientPaidCents: number | null
  value: EobAmounts
  onChange: (value: EobAmounts) => void
  insuranceDisabled?: boolean
  patientInvoices: PatientInvoiceInfo[]
  breakdown: PatientBreakdown
  onBreakdownChange: (next: PatientBreakdown) => void
}

// "Claim & EOB amounts" entry (accounting-plan Task 20, mockup 02): billers key the
// allowed amount and the patient responsibility in from the EOB; the insurance payment
// defaults to allowed − patient responsibility and is editable by the biller (lower, never
// higher — payer fees); it is emitted through onChange as the amount being recorded
// (state lives in the containing form).
// Validation lives in eob-amounts.ts — the backend re-validates on submit.
export function EobAmountsForm({
  providerChargeCents,
  patientPaidCents,
  value,
  onChange,
  insuranceDisabled,
  patientInvoices,
  breakdown,
  onBreakdownChange,
}: EobAmountsFormProps) {
  const adjustmentCents = computeAdjustmentCents(providerChargeCents, value.allowedCents)
  // Over the remaining charge: a warning only — the server asks to confirm the overpayment.
  const allowedExceedsCharge = adjustmentCents !== null && adjustmentCents < 0
  const feesCents = computeFeesCents(value)

  const locked = lockedKinds(patientInvoices)
  const invoiceOf = (kind: PatientInvoiceInfo['kind']) =>
    patientInvoices.find((invoice) => invoice.kind === kind)
  const breakdownMessage = breakdownError(breakdown, patientInvoices, value.patientRespCents)

  const emit = (patch: Partial<EobAmounts>) => {
    const next = { ...value, ...patch }
    onChange({
      ...next,
      insuranceCents: next.insuranceEdited
        ? next.insuranceCents
        : computeInsuranceCents(next.allowedCents, next.patientRespCents),
    })
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2">
        <div className="flex items-baseline gap-2">
          <Text size="sm" fw={600} c="dimmed">
            Provider charge
          </Text>
          <Text size="xs" c="dimmed">
            From claim
          </Text>
        </div>
        <Text
          size="sm"
          fw={600}
          c={providerChargeCents === null ? 'dimmed' : 'dark'}
          className="tabular-nums"
        >
          {providerChargeCents === null ? '—' : formatCents(providerChargeCents)}
        </Text>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <NumberInput
          label="Allowed"
          prefix="$"
          min={0}
          decimalScale={2}
          value={centsToDollars(value.allowedCents)}
          onChange={(v) => emit({ allowedCents: dollarsToCents(v) })}
          description={
            allowedExceedsCharge
              ? 'Exceeds the remaining charge — recording will ask you to confirm the overpayment'
              : undefined
          }
          classNames={{ description: '!text-amber-700' }}
        />
        <NumberInput
          label="Patient responsibility (Total)"
          prefix="$"
          min={0}
          decimalScale={2}
          value={centsToDollars(value.patientRespCents)}
          onChange={(v) => emit({ patientRespCents: dollarsToCents(v) })}
        />
      </div>

      <div className="grid grid-cols-3 gap-3">
        {KIND_ORDER.map((kind) => (
          <NumberInput
            key={kind}
            label={PATIENT_INVOICE_KIND_LABELS[kind]}
            prefix="$"
            min={0}
            decimalScale={2}
            value={centsToDollars(breakdown[kind])}
            disabled={locked.has(kind)}
            description={lockedHint(invoiceOf(kind)?.status) ?? undefined}
            onChange={(v) => onBreakdownChange({ ...breakdown, [kind]: dollarsToCents(v) })}
          />
        ))}
      </div>

      {breakdownMessage ? (
        <Text size="sm" c="red">
          {breakdownMessage}
        </Text>
      ) : breakdownOverpaid(patientInvoices, value.patientRespCents) ? (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
          <Text size="sm" c="dark">
            Patient paid more than their responsibility
          </Text>
        </div>
      ) : null}

      <NumberInput
        label="Insurance payment"
        description="Defaults to allowed − patient responsibility"
        prefix="$"
        min={0}
        // No blur-clamp: a clamped value would masquerade as a biller edit and stop the auto-recompute.
        clampBehavior="none"
        decimalScale={2}
        disabled={insuranceDisabled}
        value={centsToDollars(value.insuranceCents)}
        onChange={(v) => emit({ insuranceCents: dollarsToCents(v), insuranceEdited: true })}
        error={insuranceError(value) ?? undefined}
      />

      <div className="flex flex-col divide-y divide-slate-100 rounded-lg border border-slate-200">
        {feesCents !== null && feesCents > 0 ? (
          <div className="flex items-baseline justify-between gap-2 px-3 py-2">
            <div className="flex items-baseline gap-2">
              <Text size="sm" fw={600} c="dimmed">
                Payer fees
              </Text>
              <Text size="xs" c="dimmed">
                Auto-calculated
              </Text>
            </div>
            <Text size="sm" c="dark" className="tabular-nums">
              {formatCents(feesCents)}
            </Text>
          </div>
        ) : null}

        <div className="flex items-baseline justify-between gap-2 px-3 py-2">
          <div className="flex items-baseline gap-2">
            <Text size="sm" fw={600} c="dimmed">
              Adjustment
            </Text>
            <Text size="xs" c="dimmed">
              Auto-calculated
            </Text>
          </div>
          <Text
            size="sm"
            c={adjustmentCents !== null && adjustmentCents < 0 ? 'red' : 'dark'}
            className="tabular-nums"
          >
            {adjustmentCents === null ? '—' : formatCents(adjustmentCents)}
          </Text>
        </div>
      </div>

      <BalanceBadge patientRespCents={value.patientRespCents} patientPaidCents={patientPaidCents} />
    </div>
  )
}
